import React, { useRef, useEffect, useState } from 'react';
import { useForm, ValidationError } from '@formspree/react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Center, MeshTransmissionMaterial, Environment } from '@react-three/drei';
import './contact.css';

function RotatingGeometry() {
  const meshRef = useRef();

  useFrame((_, delta) => {
    if (!meshRef.current) return;
    meshRef.current.rotation.x += delta * 0.25;
    meshRef.current.rotation.y += delta * 0.35;
  });

  return (
    <Center>
      <mesh ref={meshRef}>
        <icosahedronGeometry args={[1.1, 0]} />
        <MeshTransmissionMaterial
          /* glass */
          transmission={1}
          roughness={0.2}
          thickness={0.3}
          ior={1.4}
          chromaticAberration={0.03}
          anisotropy={0.1}
          distortion={0.2}
          distortionScale={0.35}
          temporalDistortion={0.12}
          /* quality */
          samples={10}
          resolution={1024}
          backside
          backsideThickness={1.2}
          /* subtle tint */
          attenuationDistance={1.5}
          attenuationColor="#ffffff"
        />
      </mesh>
    </Center>
  );
}

function ContactForm() {
  const [state, handleSubmit] = useForm('xkgdloyw');

  if (state.succeeded) {
    return (
      <div className="contact-card">
        <p className="contact-success">Merci ! Je reviens vers vous rapidement.</p>
      </div>
    );
  }

  return (
    <form className="contact-card" onSubmit={handleSubmit}>
      <div className="contact-row">
        <div className="contact-field">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            name="email"
            placeholder="johndoe@gmail.com"
            required
          />
          <ValidationError prefix="Email" field="email" errors={state.errors} />
        </div>

        <div className="contact-field">
          <label htmlFor="name">Name</label>
          <input
            id="name"
            type="text"
            name="name"
            placeholder="John Doe"
            required
          />
          <ValidationError prefix="Name" field="name" errors={state.errors} />
        </div>
      </div>

      <div className="contact-field">
        <label htmlFor="message">Message</label>
        <textarea id="message" name="message" placeholder="Votre message" required />
        <ValidationError prefix="Message" field="message" errors={state.errors} />
      </div>

      <button className="contact-btn" type="submit" disabled={state.submitting}>
        {state.submitting ? 'Envoi…' : 'Envoyer'}
      </button>
    </form>
  );
}

function Contact() {
  const [isReady, setIsReady] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Let the first paint happen, then trigger the CSS transition
    const id = requestAnimationFrame(() => setIsReady(true));

    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  return (
    <div className={`contact-page ${isReady ? 'is-contact' : ''}`}>
      {/* Full-viewport canvas behind everything (outside .container) */}
      <Canvas
        className="contact-canvas-fixed"
        camera={{ position: [0, 0, 4.2], fov: 50 }}
        dpr={[1, 1.75]}
      >
        <ambientLight intensity={0.6} />
        <directionalLight position={[3, 3, 3]} intensity={1.0} />
        <Environment preset="city" />

        {/* Backdrop for the glass to refract */}
        {/* <mesh position={[2.2, 0.1, -2.4]} rotation={[0, 0, 0]}>
          <planeGeometry args={[8, 6]} />
          <meshStandardMaterial color="#0b0b0c" />
        </mesh> */}

        {/* Push the geometry to the right so it “floats” in the black area */}
        <group
          position={isMobile ? [0, -0.55, 0] : [0, 0, 0]}
          scale={isMobile ? 0.72 : 1}
        >
          <RotatingGeometry />
        </group>

        <OrbitControls
          makeDefault
          enableDamping
          dampingFactor={0.12}
          enablePan={false}
          enableZoom={false}
        />
      </Canvas>

      {/* Your content card inside the layout container */}
      <div className="container contact-container">
        <div className="contact-stage">
          <div className="contact-overlay" />

          <div className="contact-content">
            <h1 className="contact-title">Contact</h1>
            <p className="contact-subtitle">Un message, une mission, une idée ?</p>
            <ContactForm />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Contact;
